import { quoteSensitivityApi } from 'apiConnector/quote/sensitivity';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { toApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { useCallback, useEffect, useState } from 'react';
import type { QuoteSensitivity } from 'state/blotters/priceImprovement/priceImprovementBlotter.model';
import { httpClient } from 'utils/http/httpClient';
import type { HttpError } from 'utils/http/httpClient.model';

export type FetchStatus = 'loading' | 'error' | 'unauthorized' | 'success';

function isForbidden(httpErrorCode: number) {
  return httpErrorCode === 401 || httpErrorCode === 403;
}

const api = quoteSensitivityApi(httpClient);

const sensitivityCache = new Map<string, Readonly<QuoteSensitivity>>();

export function useFetchSensitivity(
  quoteId: string,
  shouldRefresh: boolean,
): [status: FetchStatus, data?: Readonly<QuoteSensitivity>, error?: ApiErrorMessage, onRetry?: () => void] {
  const cachedData = shouldRefresh ? undefined : sensitivityCache.get(quoteId);
  const [data, setData] = useState<Readonly<QuoteSensitivity> | undefined>(cachedData);
  const [isLoading, setIsLoading] = useState(cachedData === undefined);
  const [httpErrorCode, setHttpErrorCode] = useState<number | null>();
  const [apiErrorMessage, setApiErrorMessage] = useState<ApiErrorMessage | null>();
  const [fetchDataRetries, setFetchDataRetries] = useState(0);

  const handleRetry = useCallback(() => {
    setFetchDataRetries(fetchDataRetries + 1);
  }, [fetchDataRetries]);

  useEffect(() => {
    if (!shouldRefresh && sensitivityCache.has(quoteId)) {
      return;
    }
    setIsLoading(true);
    const subscription = api.getSensitivity(quoteId).subscribe({
      next(data) {
        sensitivityCache.set(quoteId, data);
        setIsLoading(false);
        setHttpErrorCode(null);
        setApiErrorMessage(null);
        setData(data);
      },
      error(error: HttpError) {
        setHttpErrorCode(error.status);
        setApiErrorMessage(toApiErrorMessage(error));
        setIsLoading(false);
      },
    });
    return function cleanup() {
      subscription.unsubscribe();
    };
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps  We want to rerun this effect every time fetchDataRetries is updated
  }, [fetchDataRetries, quoteId]);

  if (isLoading) {
    return ['loading'];
  }
  if (apiErrorMessage && httpErrorCode) {
    return isForbidden(httpErrorCode) ? ['unauthorized'] : ['error', undefined, apiErrorMessage, handleRetry];
  }

  return ['success', data];
}
